<template>
  <!-- A plain wrapper with NO `page-break-avoid`, matching ExecutiveSummaryModuleSection: an
       avoid here made the section indivisible whenever it happened to fit, so it moved whole and
       left most of a page blank rather than starting under the content above it. -->
  <div>
    <!-- The heading, the description and the tiles/rings row form THE page-break unit, so the
         seam can only fall between them and the lists below. Protecting only the tiles/rings row
         would strand the heading at the foot of the page instead, which is where the break
         already falls; protecting the whole section would make it indivisible and push it whole.
         Together they measure 391px, comfortably inside the 718px page, so this unit can always
         be honoured — html2pdf drops an `avoid` from anything taller than one page without
         warning, which is how a break once landed through a metric tile's headline number. -->
    <div class="page-break-avoid">
      <!-- Same faint token as ExecutiveSummaryModuleSection when SAT is not subscribed. -->
      <div class="d-flex align-center ga-2 mb-3">
        <v-icon size="40" :icon="satIcon" :class="{ 'text-indigo-medium': disabled }" />
        <span
          class="headline6"
          :class="{ 'text-indigo-medium': disabled }"
          data-testid="executive-summary-sat-title"
        >
          {{ $t("reports.executiveSummaryReport.modules.securityAwarenessTraining.title") }}
        </span>
      </div>
      <div class="body1 mb-6" :class="{ 'text-indigo-medium': disabled }">
        {{ $t("reports.executiveSummaryReport.modules.securityAwarenessTraining.description") }}
      </div>

      <executive-summary-module-disabled-state v-if="disabled" :module="satModule" />

      <div v-else class="executive-summary-sat__top mb-10">
        <div class="executive-summary-sat__metrics">
          <report-metric-tile
            :value="viewData.emailsSent"
            :label="
              $t('reports.executiveSummaryReport.securityAwarenessTraining.phishingSimulationsSent')
            "
          />
          <report-metric-tile
            :value="viewData.trainingsSent"
            :label="$t('reports.executiveSummaryReport.securityAwarenessTraining.trainingsSent')"
          />
        </div>
        <report-completion-ring
          :completed="viewData.securityTrainings.completed"
          :total="viewData.securityTrainings.total"
          :label="$t('reports.executiveSummaryReport.securityTrainingsCompletion')"
          :caption="securityCaption"
        />
        <report-completion-ring
          :completed="viewData.phishingTrainings.completed"
          :total="viewData.phishingTrainings.total"
          :label="$t('reports.executiveSummaryReport.phishingTrainingsCompletion')"
          :caption="phishingCaption"
        />
      </div>
    </div>

    <!-- Outside the unit above: this is the seam the section is allowed to split on. Gated on
         `disabled` in its own right — the placeholder replaces the body, so an unsubscribed
         module must not render these lists. -->
    <div v-if="!disabled" class="executive-summary-sat__lists">
      <div>
        <executive-summary-bar-list
          :title="$t('reports.executiveSummaryReport.securityAwarenessTraining.topPhishedUsers')"
          :items="topPhishedUsers"
          :variant="ReportTilesVariant.TEAL"
          :show-zero-values="showZeroValues"
        />
      </div>
      <div>
        <executive-summary-bar-list
          :title="$t('reports.executiveSummaryReport.securityAwarenessTraining.topOverdueUsers')"
          :items="topOverdueUsers"
          :variant="ReportTilesVariant.VIOLET"
          :show-zero-values="showZeroValues"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import ReportMetricTile from "@/components/reports/ReportMetricTile.vue";
import ReportCompletionRing from "@/components/reports/ReportCompletionRing.vue";
import ExecutiveSummaryBarList from "@/components/reports/executive-summary/ExecutiveSummaryBarList.vue";
import ExecutiveSummaryModuleDisabledState from "@/components/reports/executive-summary/ExecutiveSummaryModuleDisabledState.vue";
import { ReportTilesVariant, REPORT_MODULE_ICONS_S, type ReportModule } from "@/constants/reports";
import { SubscriptionModule } from "@/constants/workplaces";
import { isModuleDisabled } from "@/_helpers/subscription";
import { formatReportNumber } from "@/_helpers/reports";
import type {
  ExecutiveSummarySecurityAwarenessReportData,
  SecurityAwarenessUserFailureRecord,
} from "@/_store/reports.module";

/**
 * Reshapes the security-awareness failure records into the shape the shared bar list consumes.
 * Sorted by offence count descending: the backend sends these unordered, and both lists are titled
 * "Top …", so raw order would put a two-offence user above a nine-offence one.
 * @param records - Per-user failure records, as sent for top phished and top overdue users.
 * @returns Bar list items, highest offence count first, keyed by the user's id.
 */
const toBarListItems = (records: SecurityAwarenessUserFailureRecord[]) =>
  records
    .map(({ userId, offenses }) => ({ id: userId, label: userId, value: offenses }))
    .sort((a, b) => b.value - a.value);

export default defineComponent({
  components: {
    ReportMetricTile,
    ReportCompletionRing,
    ExecutiveSummaryBarList,
    ExecutiveSummaryModuleDisabledState,
  },
  props: {
    data: {
      type: Object as PropType<ExecutiveSummarySecurityAwarenessReportData>,
      required: true,
    },
    showZeroValues: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    /**
     * The payload with every field zero-filled.
     *
     * SAT is the only section with no adapter, so nothing zero-filled it and the template read two
     * levels into `securityTrainings` / `phishingTrainings` — fields this redesign introduced and
     * the backend does not send yet. A throw in a render is an error no `v-if` can catch: it
     * blanks the whole report card, not just this section. `toBarListItems` had the same exposure,
     * mapping over an absent array.
     *
     * Nested fields are defaulted individually, not the objects as a whole, so a half-filled
     * `{ completed: 5 }` is covered too. The deeper fix is an
     * `adaptExecutiveSummarySecurityAwareness` beside the other two adapters; this holds the
     * guarantee locally until that exists.
     */
    const viewData = computed<ExecutiveSummarySecurityAwarenessReportData>(() => ({
      emailsSent: props.data?.emailsSent ?? 0,
      trainingsSent: props.data?.trainingsSent ?? 0,
      securityTrainings: {
        completed: props.data?.securityTrainings?.completed ?? 0,
        total: props.data?.securityTrainings?.total ?? 0,
      },
      phishingTrainings: {
        completed: props.data?.phishingTrainings?.completed ?? 0,
        total: props.data?.phishingTrainings?.total ?? 0,
      },
      topUsersFailedPhishing: props.data?.topUsersFailedPhishing ?? [],
      topUsersFailedTraining: props.data?.topUsersFailedTraining ?? [],
    }));
    const i18n = useI18n();
    const satModule: ReportModule = SubscriptionModule.SECURITY_AWARENESS_TRAINING;

    const disabled = computed(() => isModuleDisabled(satModule));

    const securityCaption = computed(() =>
      i18n.t("reports.executiveSummaryReport.securityTrainingsCompletionCaption", {
        completed: formatReportNumber(viewData.value.securityTrainings.completed),
        total: formatReportNumber(viewData.value.securityTrainings.total),
      })
    );

    const phishingCaption = computed(() =>
      i18n.t("reports.executiveSummaryReport.phishingTrainingsCompletionCaption", {
        completed: formatReportNumber(viewData.value.phishingTrainings.completed),
        total: formatReportNumber(viewData.value.phishingTrainings.total),
      })
    );

    const topPhishedUsers = computed(() => toBarListItems(viewData.value.topUsersFailedPhishing));
    const topOverdueUsers = computed(() => toBarListItems(viewData.value.topUsersFailedTraining));

    return {
      viewData,
      satModule,
      satIcon: REPORT_MODULE_ICONS_S[satModule],
      disabled,
      securityCaption,
      phishingCaption,
      topPhishedUsers,
      topOverdueUsers,
      ReportTilesVariant,
    };
  },
});
</script>

<style scoped lang="scss">
// An icon-font glyph pins `color: #262260` on its own `:before` (see `.icon-security-awerness-training-s`
// in @coronet/cui's icons.css), and a declaration on the pseudo-element outranks anything the element
// merely inherits — so `text-indigo-medium` on the `v-icon` above was applied and changed nothing,
// leaving an unsubscribed SAT icon at full-strength Primary/Indigo beside its muted title. Keyed
// off the class rather than the icon, so an icon carrying no `text-*` class keeps the solid colour
// its own `:before` gives it instead of picking up `v-icon`'s 87% emphasis. Same rule as
// ExecutiveSummaryImpactCard and ExecutiveSummaryModuleSection — three scoped copies, because the
// root cause is the colour pins @coronet/cui's icon generator writes for almost every glyph; unpinning
// them belongs in the library and is its own change.
.v-icon.text-indigo-medium::before {
  color: inherit;
}

.executive-summary-sat__top {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

// The grid already stretches this column to the row's height — set by the taller ring cards —
// but the two tiles inside it are content-height, so the pair stopped short of the rings'
// bottom edge. `flex: 1` (basis 0, not `flex-grow` alone) splits the column evenly between
// them, so the two tiles together end level with the ring beside them whatever the rings
// measure.
.executive-summary-sat__metrics {
  display: flex;
  flex-direction: column;
  gap: 16px;

  > * {
    flex: 1;
  }
}

// A plain grid, matching ExecutiveSummaryModuleSection: Vuetify's v-row cancels its columns'
// padding with `margin: -12px`, which both spills past the section and stacks a second gutter
// on top of the `gap`.
.executive-summary-sat__lists {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 40px;
}
</style>
